import React, { useMemo } from "react";
import {
  Mail,
  Lock,
  User as UserIcon,
  ArrowRight,
  Dumbbell,
  Users,
} from "lucide-react";
import {
  SIGNUP_INTENT_BOTH,
  SIGNUP_INTENT_PLAYER,
  SIGNUP_INTENT_TRAINER,
  resolveSignupRegisterHintIds,
  resolveSignupRegisterHintI18nKey,
} from "@tennis-matches/services/auth";
import { AuthPasswordToggle, AuthTextField } from "./AuthPageVisuals.jsx";
import { RegisterPasswordRules } from "./RegisterPasswordRules.jsx";
import "./PublicRegisterSimple.css";

const SIGNUP_INTENT_OPTIONS = [
  {
    id: SIGNUP_INTENT_PLAYER,
    icon: UserIcon,
    titleKey: "auth.register.signupIntent.player.title",
    descriptionKey: "auth.register.signupIntent.player.description",
  },
  {
    id: SIGNUP_INTENT_TRAINER,
    icon: Dumbbell,
    titleKey: "auth.register.signupIntent.trainer.title",
    descriptionKey: "auth.register.signupIntent.trainer.description",
  },
  {
    id: SIGNUP_INTENT_BOTH,
    icon: Users,
    titleKey: "auth.register.signupIntent.both.title",
    descriptionKey: "auth.register.signupIntent.both.description",
  },
];

/**
 * Präsentational: keine Validierung, kein Email-API-Call.
 * Erwartet den Block aus usePublicRegistrationForm().formProps
 */
export function PublicRegisterSimple({
  t,
  busy,
  values,
  setters,
  inlineErrors = {},
  clearField,
  onSubmit,
  onSwitchToLogin,
  emailLiveStatus = "idle",
  emailLiveBackendUnavailable = false,
  showPassword,
  showPassword2,
  toggleShowPassword,
  toggleShowPassword2,
}) {
  const {
    email,
    password,
    password2,
    firstName,
    lastName,
    signupIntent,
    consentPrivacy,
    consentTerms,
  } = values || {};

  const {
    setEmail,
    setPassword,
    setPassword2,
    setFirstName,
    setLastName,
    setSignupIntent,
    setConsentPrivacy,
    setConsentTerms,
  } = setters || {};

  const registerHintIds = useMemo(
    () => resolveSignupRegisterHintIds(signupIntent),
    [signupIntent],
  );

  const formBusy = busy === "register-public" || Boolean(busy);
  const emailTakenLive = emailLiveStatus === "taken";
  const emailCheckingLive = emailLiveStatus === "checking";

  return (
    <div className="publicRegisterSimple">
      <div className="publicRegisterSimple__fields">
        <AuthTextField
          id="auth-register-email"
          label={t("auth.register.email")}
          type="email"
          value={email}
          disabled={formBusy}
          autoComplete="email"
          leadingIcon={Mail}
          onChange={(e) => {
            clearField?.("email");
            setEmail?.(String(e.target.value || ""));
          }}
        />
        {inlineErrors.email ? (
          <p className="publicRegisterSimple__fieldError" role="alert">
            {inlineErrors.email}
          </p>
        ) : null}
        {emailLiveBackendUnavailable ? (
          <p className="publicRegisterSimple__fieldHint" role="status">
            {t("auth.register.emailCheck.errorBackendUnavailable")}
          </p>
        ) : null}
        {emailCheckingLive ? (
          <p className="publicRegisterSimple__fieldHint" role="status">
            {t("auth.register.emailCheck.checking")}
          </p>
        ) : null}
        {emailTakenLive && typeof onSwitchToLogin === "function" ? (
          <p className="publicRegisterSimple__inlineLinkRow">
            <button
              type="button"
              className="publicRegisterSimple__inlineLink"
              onClick={onSwitchToLogin}
            >
              {t("auth.page.footerLogin")}
            </button>
          </p>
        ) : null}

        <div className="publicRegisterSimple__row">
          <div className="publicRegisterSimple__rowField">
            <AuthTextField
              id="auth-register-first"
              label={t("auth.register.firstName")}
              type="text"
              value={firstName}
              disabled={formBusy}
              autoComplete="given-name"
              leadingIcon={UserIcon}
              onChange={(e) => {
                clearField?.("firstName");
                setFirstName?.(String(e.target.value || ""));
              }}
            />
            {inlineErrors.firstName ? (
              <p className="publicRegisterSimple__fieldError">
                {inlineErrors.firstName}
              </p>
            ) : null}
          </div>
          <div className="publicRegisterSimple__rowField">
            <AuthTextField
              id="auth-register-last"
              label={t("auth.register.lastName")}
              type="text"
              value={lastName}
              disabled={formBusy}
              autoComplete="family-name"
              onChange={(e) => {
                clearField?.("lastName");
                setLastName?.(String(e.target.value || ""));
              }}
            />
            {inlineErrors.lastName ? (
              <p className="publicRegisterSimple__fieldError">
                {inlineErrors.lastName}
              </p>
            ) : null}
          </div>
        </div>

        <div
          className="publicRegisterSimple__intent"
          role="radiogroup"
          aria-labelledby="publicRegisterSimple-intent-heading"
        >
          <h4
            id="publicRegisterSimple-intent-heading"
            className="publicRegisterSimple__intentHeading"
          >
            {t("auth.register.signupIntent.heading")}
          </h4>
          <div className="publicRegisterSimple__intentGrid">
            {SIGNUP_INTENT_OPTIONS.map((option) => {
              const Icon = option.icon;
              const selected = signupIntent === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  disabled={formBusy}
                  className={[
                    "publicRegisterSimple__intentCard",
                    selected
                      ? "publicRegisterSimple__intentCard--selected"
                      : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => {
                    clearField?.("signupIntent");
                    setSignupIntent?.(option.id);
                  }}
                >
                  <span
                    className="publicRegisterSimple__intentIcon"
                    aria-hidden="true"
                  >
                    <Icon size={20} strokeWidth={1.8} />
                  </span>
                  <span className="publicRegisterSimple__intentTitle">
                    {t(option.titleKey)}
                  </span>
                  <span className="publicRegisterSimple__intentDesc">
                    {t(option.descriptionKey)}
                  </span>
                </button>
              );
            })}
          </div>
          {inlineErrors.signupIntent ? (
            <p className="publicRegisterSimple__fieldError" role="alert">
              {inlineErrors.signupIntent}
            </p>
          ) : null}
        </div>

        <AuthTextField
          id="auth-register-password"
          label={t("auth.register.password")}
          type={showPassword ? "text" : "password"}
          value={password}
          disabled={formBusy}
          autoComplete="new-password"
          leadingIcon={Lock}
          trailing={
            <AuthPasswordToggle
              visible={showPassword}
              onToggle={toggleShowPassword}
              labelShow={t("auth.login.showPassword")}
              labelHide={t("auth.login.hidePassword")}
            />
          }
          onChange={(e) => {
            clearField?.("password");
            clearField?.("password2");
            setPassword?.(String(e.target.value || ""));
          }}
        />
        {inlineErrors.password ? (
          <p className="publicRegisterSimple__fieldError">
            {inlineErrors.password}
          </p>
        ) : null}

        <AuthTextField
          id="auth-register-password-confirm"
          label={t("auth.register.passwordConfirm")}
          type={showPassword2 ? "text" : "password"}
          value={password2}
          disabled={formBusy}
          autoComplete="new-password"
          leadingIcon={Lock}
          trailing={
            <AuthPasswordToggle
              visible={showPassword2}
              onToggle={toggleShowPassword2}
              labelShow={t("auth.login.showPassword")}
              labelHide={t("auth.login.hidePassword")}
            />
          }
          onChange={(e) => {
            clearField?.("password2");
            setPassword2?.(String(e.target.value || ""));
          }}
        />
        {inlineErrors.password2 ? (
          <p className="publicRegisterSimple__fieldError">
            {inlineErrors.password2}
          </p>
        ) : null}

        <RegisterPasswordRules
          t={t}
          password={password}
          passwordConfirm={password2}
        />
      </div>

      <div
        className="publicRegisterSimple__consent"
        role="group"
        aria-labelledby="publicRegisterSimple-consent-heading"
      >
        <h4
          id="publicRegisterSimple-consent-heading"
          className="publicRegisterSimple__consentHeading"
        >
          {t("auth.register.consent.heading")}
        </h4>
        <label className="publicRegisterSimple__consentRow">
          <input
            type="checkbox"
            checked={Boolean(consentPrivacy)}
            disabled={formBusy}
            onChange={(e) => {
              setConsentPrivacy?.(e.target.checked);
              clearField?.("consentPrivacy");
            }}
          />
          <span>
            {t("auth.register.consent.privacyPrefix")}{" "}
            <a
              className="publicRegisterSimple__consentLink"
              href="/datenschutz"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("footer.privacy")}
            </a>
            {t("auth.register.consent.privacySuffix")}
          </span>
        </label>
        {inlineErrors.consentPrivacy ? (
          <p className="publicRegisterSimple__fieldError" role="alert">
            {inlineErrors.consentPrivacy}
          </p>
        ) : null}
        <label className="publicRegisterSimple__consentRow">
          <input
            type="checkbox"
            checked={Boolean(consentTerms)}
            disabled={formBusy}
            onChange={(e) => {
              setConsentTerms?.(e.target.checked);
              clearField?.("consentTerms");
            }}
          />
          <span>
            {t("auth.register.consent.termsPrefix")}{" "}
            <a
              className="publicRegisterSimple__consentLink"
              href="/agb"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("auth.register.consent.termsLinkLabel")}
            </a>
            {t("auth.register.consent.termsSuffix")}
          </span>
        </label>
        {inlineErrors.consentTerms ? (
          <p className="publicRegisterSimple__fieldError" role="alert">
            {inlineErrors.consentTerms}
          </p>
        ) : null}
      </div>

      <button
        type="button"
        className="authPage__submitBtn publicRegisterSimple__submit"
        disabled={formBusy}
        onClick={() => void onSubmit?.()}
      >
        {formBusy ? t("common.saving") : t("auth.register.simple.submit")}
        {!formBusy ? (
          <ArrowRight size={16} strokeWidth={2} aria-hidden="true" />
        ) : null}
      </button>

      <div className="publicRegisterSimple__hintBlock">
        <p className="publicRegisterSimple__hintTitle">
          {registerHintIds.length
            ? t("auth.register.simple.hintsTitle")
            : t("auth.register.simple.hintsChooseIntent")}
        </p>
        {registerHintIds.length ? (
          <ul className="publicRegisterSimple__hintList">
            {registerHintIds.map((hintId) => (
              <li key={hintId}>
                {t(resolveSignupRegisterHintI18nKey(hintId))}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
}
